import { initializeApp } from 'firebase/app';
import { getAuth, initializeAuth, indexedDBLocalPersistence } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { 
  initializeFirestore, 
  persistentLocalCache, 
  persistentMultipleTabManager
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service.
// On native, getAuth() would install the browser popup/redirect resolver, which
// pulls gapi from apis.google.com to build an iframe against authDomain. Under
// the capacitor://localhost origin that script fails, and because the failure is
// swallowed, auth never finishes initializing — onAuthStateChanged never fires
// and the app hangs on the loading screen forever. We sign in with custom tokens
// (see AuthContext OTP flow), so the resolver is dead weight: initializeAuth with
// only indexedDB persistence skips it entirely.
export const auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, { persistence: indexedDBLocalPersistence })
  : getAuth(app);

// Initialize Cloud Firestore with modern cache API
// This replaces the deprecated enableIndexedDbPersistence()
// Provides:
// - Automatic offline persistence across app restarts
// - Multi-tab synchronization
// - Better performance and reliability
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  }),
  // Force request/response long-polling instead of WebChannel streaming.
  // Capacitor's CapacitorHttp plugin patches the WebView's fetch/XHR to route
  // through native OkHttp, which can't proxy Firestore's streaming transport —
  // causing "Failed to get document because the client is offline" in packaged
  // (release/App Distribution) builds. See capacitor.config.ts CapacitorHttp.
  experimentalForceLongPolling: true
});

// Initialize Firebase Storage and get a reference to the service
export const storage = getStorage(app);

// Initialize Cloud Functions (asia-south1 region)
export const functions = getFunctions(app, 'asia-south1');

// Identifies this client to the shared auth Cloud Functions, which are also
// called by the LoanLens Dashboard and apply a different role policy per app.
// See functions/src/lib/appPolicy.ts.
export const APP_ID = 'dsalens';

// Connect to Functions emulator in development
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

// Export Firebase app instance
export default app;
